import { Delete } from '@mui/icons-material';
import { Grid, IconButton } from '@mui/material';
import React, { FC, useMemo } from 'react';
import useBrandGroupEntitlements from '../../../hooks/useBrandGroupEntitlements';
import { ITypeAhead } from '../../../types/type-ahead';
import CustomAutoComplete from '../../General/CustomAutoComplete';
import CustomDatePicker from '../../General/CustomDatePicker';
import { Switch } from '../../Switch';
import { Claim } from '../../../types/entitlements';

export type ClaimError = {
  brand?: string;
  code?: string;
  validFrom?: string;
  validTo?: string;
};

type ClaimValues = Partial<Claim>;

type ClaimFieldProps = {
  formikId: string;
  error?: ClaimError;
  values: ClaimValues;
  setFieldValue: <T>(field: string, value: T, shouldValidate?: boolean) => void;
  onRemove?: () => void;
};

export const ClaimField: FC<ClaimFieldProps> = ({
  formikId,
  values,
  error,
  setFieldValue,
  onRemove,
}) => {
  const { getEntitlementOptionList, getBrandOptionList, getHumanReadableBrandFromBrandCode } =
    useBrandGroupEntitlements();

  const brandCodeField: ITypeAhead | null = useMemo(() => {
    if (!values.brand) {
      return null;
    }
    return {
      label: getHumanReadableBrandFromBrandCode(values.brand),
      key: values.brand,
    };
  }, [getHumanReadableBrandFromBrandCode, values.brand]);

  const codeField: ITypeAhead | null = useMemo(() => {
    if (!values.code) {
      return null;
    }
    return {
      label: values.code,
      key: values.code,
    };
  }, [values.code]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md>
        <CustomAutoComplete
          label="Brand"
          name={`${formikId}.brand`}
          data-cy={`${formikId}.brand`}
          error={error?.brand !== undefined}
          helperText={error?.brand}
          options={getBrandOptionList()}
          setFieldValue={setFieldValue}
          fieldValue={brandCodeField}
          style={{ minWidth: '205px' }}
        />
      </Grid>
      <Grid item xs={12} md>
        <CustomAutoComplete
          label="Code"
          name={`${formikId}.code`}
          data-cy={`${formikId}.code`}
          error={error?.code !== undefined}
          helperText={error?.code}
          options={getEntitlementOptionList(values.brand)}
          setFieldValue={setFieldValue}
          fieldValue={codeField}
          style={{ minWidth: '205px' }}
        />
      </Grid>
      <Grid item xs={12} md>
        <CustomDatePicker
          label={'Valid from'}
          data-cy={`${formikId}.validFrom`}
          time={{ hour: 0, minute: 0, second: 0 }}
          value={values.validFrom}
          error={error?.validFrom}
          onChange={(date) => setFieldValue(`${formikId}.validFrom`, date?.toISOString())}
        />
      </Grid>
      <Grid item xs={12} md>
        <CustomDatePicker
          label={'Valid to'}
          data-cy={`${formikId}.validTo`}
          time={{ hour: 23, minute: 59, second: 59 }}
          value={values.validTo}
          error={error?.validTo}
          onChange={(date) => setFieldValue(`${formikId}.validTo`, date?.toISOString())}
        />
      </Grid>
      <Grid item xs={12} md>
        <Switch
          label="Active"
          checked={values.isActive ?? true}
          onChange={(value) => setFieldValue(`${formikId}.isActive`, value)}
        />
      </Grid>
      <Grid item xs={2} md={1}>
        {onRemove && (
          <IconButton aria-label="delete" onClick={onRemove}>
            <Delete />
          </IconButton>
        )}
      </Grid>
    </Grid>
  );
};
